import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BcModal from './BcModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Bc {
  bc_id: number;
  bc_name: string;
  bc_status: number;
}

const Bc: React.FC = () => {
  const [bcs, setBcs] = useState<Bc[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Bc | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBc = async () => {
    try {
      const data = await apiRequest<Bc[]>({
        method: 'get',
        url: 'base-colors',
      });
      setBcs(data);
    } catch (error) {
      showError('Failed to fetch Base Colors');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBc();
  }, []);

  const handleEditClick = (record: Bc) => {
    setIsEditMode(true);
    setInitialValues({
      bc_id: record.bc_id,
      bc_name: record.bc_name,
      bc_status: record.bc_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBc();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (bc_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-base-color/${bc_id}`,
      });
      showSuccess('Base Color deleted');
      fetchBc();
    } catch {
      showError('Failed to delete Base Color');
    }
  };

  const handleStatusToggle = async (bcId: number) => {
    setBcs((prev) =>
      prev.map((item) =>
        item.bc_id === bcId
          ? { ...item, bc_status: item.bc_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-base-color/${bcId}`,
      });
      showSuccess('Base Color status updated');
    } catch (error) {
      showError('Failed to update status');
      setBcs((prev) =>
        prev.map((item) =>
          item.bc_id === bcId
            ? { ...item, bc_status: item.bc_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Bc>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Bc> = [
    {
      title: 'Base Color',
      dataIndex: 'bc_name',
      key: 'bc_name',
      filters: Array.from(new Set(bcs.map((s) => s.bc_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.bc_name || null,
      onFilter: (value, record) => record.bc_name.includes(value as string),
      sorter: (a, b) => a.bc_name.localeCompare(b.bc_name),
      sortOrder: sortedInfo.columnKey === 'bc_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              backgroundColor: record.bc_name,
              width: 20,
              height: 20,
              borderRadius: 4,
              border: '1px solid #ccc',
            }}
          />
          <span>{record.bc_name}</span>
        </div>
      )
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Bc) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.bc_status}
                onChange={() => handleStatusToggle(record.bc_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Base Color?"
              onConfirm={() => handleDelete(record.bc_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Base Color</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Base Color
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Bc>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="bc_id"
          dataSource={bcs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Base Color found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, bcs.length)} of{' '}
                {bcs.length}
              </span>
              <Pagination
                {...pagination}
                total={bcs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BcModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Bc;